import { Component, OnInit } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { FeatureName } from '@app/core/interfaces/c5/feature-name';
import { ContextSwitchTypeaheadEntry } from '@app/menu/components/context-switcher/context-switch-typeahead-entry';
import { MenuService } from '@app/menu/services/menu.service';
import { CacheService } from '@app/portal/services/cache.service';
import { CurrentRouteData } from '@app/routing/models/current-route-data';
import { RouterService } from '@app/routing/router.service';
import { UserFeatures } from '@app/shared/models/user-features';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { TypeaheadEntry } from '@app/shared/services/typeahead-basket.service';
import { loggerFor } from '@app/utils/logger';
import { formatUserName } from '@app/utils/user';
import { Authorization } from '@business/end-user/models/authorization';
import { EndUserLite } from '@business/end-user/models/end-user-lite';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'portal-end-user-switcher',
  templateUrl: './end-user-switcher.component.html',
  styles: [],
  standalone: false,
})
export class EndUserSwitcherComponent implements OnInit {
  public entries$!: Observable<TypeaheadEntry[]>;

  public isAllowedToSwitch: boolean = false;
  public isEndUser: boolean = false;

  private logger = loggerFor('EndUserBreadcrumb');

  /**
   * If the user is currently at one of these routes then the breadcrumb select will mark all
   * entries as "to be skipped" if the user of the entry has no full authorization.
   */
  private routesToSkip = [
    'end-user-devices',
    'end-user-distribute-function-keys',
    'end-user-function-keys',
    'end-user-ipfilter',
  ];

  constructor(
    private authenticationService: AuthenticationService,
    private cacheEvictionService: CacheService,
    private enterpriseContext: EnterpriseContextService,
    private menuService: MenuService,
    private routerService: RouterService
  ) {}

  ngOnInit() {
    this.isEndUser = this.authenticationService.userIsEndUser();

    // Fetch and remember the current end user, so we can highlight the current user
    this.isAllowedToSwitch =
      this.authenticationService.userIsEnterpriseAdmin() ||
      this.authenticationService.userIsServiceProviderAdmin();

    this.entries$ = combineLatest([
      this.enterpriseContext.endUsers.data$,
      this.enterpriseContext.userFeatures.data$,
      this.routerService.currentRouteData$,
    ]).pipe(
      map(([endUsers, userFeatures, routeData]) => {
        this.logger.debug(`Update entries for route ${endUsers}`, routeData.name);

        return endUsers.map((endUser: EndUserLite): ContextSwitchTypeaheadEntry => {
          const shouldSkip = this.calculateSkip(endUser, routeData, userFeatures);
          let shouldWarn = false;

          if (!shouldSkip) {
            if (
              routeData.name === 'end-user-rich-presence-profiles' &&
              endUser.presenceProfileActive === false
            ) {
              shouldWarn = true;
            }
          }

          return {
            key: `${endUser.loginName} ${endUser.firstName} ${endUser.lastName}`,
            label: `${endUser.loginName} <small>${formatUserName(endUser)}</small>`,
            meta: {
              endUser,
              skip: shouldSkip,
              warn: shouldWarn ? 'user.breadcrumbSwitcher.disabled.noRichPresenceProfiles' : null,
            },
            value: endUser.loginName,
          };
        });
      }),
      untilDestroyed(this)
    );
  }

  /**
   * Close the dropdown and dispatch a SwitchCurrentRoute event
   * @param typeaheadEntry
   */
  trigger(typeaheadEntry: TypeaheadEntry) {
    this.cacheEvictionService.evictUserCache();

    this.menuService.switch('userId', {
      userId: typeaheadEntry.value,
    });
  }

  private calculateSkip(
    endUser: EndUserLite,
    routeData: CurrentRouteData,
    userFeatures: UserFeatures
  ): boolean | string | undefined {
    let skip: boolean | string | undefined;

    if (endUser.auth !== Authorization.FULL && this.routesToSkip.includes(routeData.name)) {
      skip = 'user.breadcrumbSwitcher.disabled';
    } else if (
      routeData.name === 'end-user-rich-presence-profiles' ||
      routeData.name === 'end-user-rich-presence-profile'
    ) {
      const userHasRichPresence = userFeatures.userMap[endUser.id].some(
        (feature) => feature.name === FeatureName.RICH_PRESENCE
      );

      if (userHasRichPresence === false) {
        skip = 'user.breadcrumbSwitcher.disabled.noRichPresence';
      }
    } else if (routeData.name === 'end-user-ms365-presence') {
      const userHasMs365Presence = userFeatures.userMap[endUser.id].some(
        (feature) => feature.name === FeatureName.MS_365_PRESENCE
      );

      if (userHasMs365Presence === false) {
        skip = 'user.breadcrumbSwitcher.disabled.noMs365Presence';
      }
    }

    return skip;
  }
}
